import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/*
 AddBlog - MS Word like editor (no external deps).
 - Uncontrolled contentEditable editor to avoid caret issues.
 - Toolbar buttons use document.execCommand where supported.
 - Emits content on blur and when Save is clicked.
 - Accepts optional onSave({ heading, subHeading, contentHtml }) prop.
*/

function execCommand(cmd, value = null) {
	try {
		document.execCommand(cmd, false, value);
	} catch (e) {
		// silent fallback
	}
}

function ColorInput({ label, command }) {
	const [value, setValue] = useState("#000000");
	return (
		<label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
			{label}
			<input
				type="color"
				value={value}
				onChange={(e) => {
					setValue(e.target.value);
					execCommand(command, e.target.value);
				}}
				style={{ width: 34, height: 26, padding: 0, border: "none", background: "transparent" }}
			/>
		</label>
	);
}

function Toolbar({ onInsertImage, onToggleHtml, isHtmlMode }) {
	return (
		<div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: 8, background: "#f5f5f5", border: "1px solid #e6e6e6", borderRadius: 6 }}>
			{/* Font / Size */}
			<select onChange={(e) => execCommand("fontName", e.target.value)} defaultValue="">
				<option value="" disabled>Font</option>
				<option>Arial</option>
				<option>Georgia</option>
				<option>Times New Roman</option>
				<option>Courier New</option>
				<option>Verdana</option>
			</select>

			<select onChange={(e) => execCommand("fontSize", e.target.value)} defaultValue="">
				<option value="" disabled>Size</option>
				{/* execCommand fontSize accepts 1-7 in many browsers; map visually */}
				<option value="3">Normal</option>
				<option value="4">Large</option>
				<option value="5">Larger</option>
			</select>

			{/* Basic styles */}
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("bold")} title="Bold"><b>B</b></button>
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("italic")} title="Italic"><i>I</i></button>
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("underline")} title="Underline"><u>U</u></button>
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("strikeThrough")} title="Strike">S</button>

			{/* Colors */}
			<ColorInput label="A" command="foreColor" />
			<ColorInput label="▔" command="hiliteColor" />

			{/* Alignment */}
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("justifyLeft")} title="Left">⟵</button>
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("justifyCenter")} title="Center">≡</button>
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("justifyRight")} title="Right">⟶</button>
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("justifyFull")} title="Justify">☰</button>

			{/* Lists / Indent */}
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("insertUnorderedList")} title="Bulleted list">•</button>
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("insertOrderedList")} title="Numbered list">1.</button>
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("outdent")} title="Outdent">⇤</button>
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("indent")} title="Indent">⇥</button>

			{/* Link / Image */}
			<button
				type="button"
				onMouseDown={(e) => e.preventDefault()}
				onClick={() => {
					const url = prompt("Enter URL", "https://");
					if (url) execCommand("createLink", url);
				}}
				title="Insert Link"
			>Link</button>

			<button
				type="button"
				onMouseDown={(e) => e.preventDefault()}
				onClick={() => {
					const url = prompt("Insert image URL", "https://");
					if (url) onInsertImage(url);
				}}
				title="Insert Image"
			>Image</button>

			{/* Undo/Redo / Clear */}
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("undo")} title="Undo">↶</button>
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand("redo")} title="Redo">↷</button>
			<button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { execCommand("removeFormat"); }} title="Clear Formatting">CLR</button>

			{/* HTML Toggle */}
			<button
				type="button"
				onMouseDown={(e) => e.preventDefault()}
				onClick={onToggleHtml}
				style={{ marginLeft: "auto", background: isHtmlMode ? "#efefef" : "transparent" }}
			>{isHtmlMode ? "HTML" : "HTML"}</button>
		</div>
	);
}
export default function AddBlog({ openDialog, onClose } = {}) {
	const navigate = useNavigate();
	const editorRef = useRef(null);
	const [heading, setHeading] = useState("");
	const [subHeading, setSubHeading] = useState("");
	const [editorName, setEditorName] = useState("");
	const [contentHtml, setContentHtml] = useState("");
	const [isHtmlMode, setIsHtmlMode] = useState(false);

	// Set content into editor when contentHtml changes programmatically
	useEffect(() => {
		if (editorRef.current && editorRef.current.innerHTML !== contentHtml) {
			editorRef.current.innerHTML = contentHtml;
		}
	}, [contentHtml]);

	function handleInsertImage(url) {
		// insert image at caret position
		execCommand("insertImage", url);
	}

	function handleBlur() {
		const html = editorRef.current?.innerHTML || "";
		setContentHtml(html);
	}

	function handleToggleHtml() {
		if (!isHtmlMode) {
			// entering HTML mode: read current HTML
			setContentHtml(editorRef.current?.innerHTML || "");
		} else {
			// leaving HTML mode: write HTML back to editor
			if (editorRef.current) editorRef.current.innerHTML = contentHtml;
		}
		setIsHtmlMode(!isHtmlMode);
	}

	// async function handleSave() {
	// 	const html = isHtmlMode ? contentHtml : (editorRef.current?.innerHTML || "");
	// 	const payload = { heading: (heading || "").trim(), subHeading: subHeading || "", editorName, content: html };
	// 	if (onClose && typeof onClose === "function") {
	// 		await onClose(payload);
	// 	} else {
	// 		// default: log to console
	// 		console.log("Saved blog:", payload);
	// 	}
	// 	// keep data in editor (consumer can close dialog)
	// }


    async function handleSave() {
		const token = localStorage.getItem("token");
		try {
			const html = isHtmlMode ? contentHtml : (editorRef.current?.innerHTML || "");
			// update: PUT to backend update endpoint (adjust path if your backend differs)
			const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/blogs/add`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
						Authorization: token ? `Bearer ${token}` : "",
					},
					body: JSON.stringify({ heading: heading, subHeading: subHeading, content: html, editorName: editorName }),
				});
				if (!res.ok) throw new Error("Update failed");
				// refresh current page after update
				// await fetchBlogs(page, rowsPerPage);
                navigate('/blogs');
        } catch (err) {
            console.error(err);
            alert("Failed to save blog. See console for details.");
            // closeDialog();
        }
	}

	return (
		<div style={{ maxWidth: 1000, margin: "12px auto", fontFamily: "Arial, sans-serif" }}>
			<div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
				<input placeholder="Heading" value={heading} onChange={e => setHeading(e.target.value)} style={{ flex: 1, padding: 8 }} />
				<input placeholder="Blog category" value={subHeading} onChange={e => setSubHeading(e.target.value)} style={{ flex: 1, padding: 8 }} />
				<input placeholder="Editor name" value={editorName} onChange={e => setEditorName(e.target.value)} style={{ flex: 1, padding: 8 }} />
			</div>

			<Toolbar onInsertImage={handleInsertImage} onToggleHtml={handleToggleHtml} isHtmlMode={isHtmlMode} />

			<div style={{ marginTop: 8, marginBottom: 12 }}>
				{isHtmlMode ? (
					<textarea
						value={contentHtml}
						onChange={(e) => setContentHtml(e.target.value)}
						style={{ width: "100%", minHeight: 300, padding: 10, fontFamily: "monospace" }}
					/>
				) : (
					<div
						ref={editorRef}
						contentEditable
						suppressContentEditableWarning
						onBlur={handleBlur}
						style={{ minHeight: 320, padding: 12, border: "1px solid #ddd", borderRadius: 4, background: "#fff" }}
                        />
                    )}
			</div>
            <button onClick={handleSave} style={{ padding: "8px 12px" }}>Save</button>
		</div>
	);
}
